<template>
  <div @click="cancelCheck()" class="game-input-on-background-wrapper" :style="getStyle(startGameConfig)">
    <div
      v-for="(image, index) in imageData"
      :key="'image_' + index"
      class="main-image-container"
      :style="getImageStyle(image)"
    ></div>
    <tr-static-text
      :static-text="item"
      class="text-element"
      v-for="(item, index) in staticText"
      :key="'text_' + index"
      :style="getStaticTextStyle(item)"
    >
    </tr-static-text>
    <tr-box
      v-if="words.length"
      :items="words"
      :config="boxConfig"
      :font-size="boxConfigFontSize"
      :style="getBoxStyle(boxConfig)"
      class="box-style"
    ></tr-box>
    <tr-input
      v-for="(item, index) in items"
      :id="item.id"
      :key="item.id"
      :config="item"
      :audio-data="{ audioSrc: item.audioSrc, audioCss: item.audioCss }"
      :tr-input-value="inputData[index]"
      :input-width="item.inputWidth || startGameConfig.inputWidth"
      :input-height="item.inputHeight || startGameConfig.inputHeight"
      :input-font-size="item.inputFontSize || startGameConfig.inputFontSize"
      :input-font-style="item.inputFontStyle || startGameConfig.inputFontStyle"
      :input-check-style="item.inputCheckStyle || {}"
      :max-length="item.inputMaxLength || startGameConfig.inputMaxLength"
      :style="getCustomStyle(item)"
    ></tr-input>
    <div v-if="showPlayer" class="audio" :style="audioPlayerStyle(startGameConfig.audioPlayer)">
      <audio
        id="playerAudio"
        controls
        crossorigin
        playsinline
        controlsList="nodownload noplaybackrate"
        :class="{ audioWhite: whiteColor }"
      >
        <source :src="giveMeAudio(audioFile)" type="audio/ogg" />
        <source :src="giveMeAudio(audioFile)" type="audio/mpeg" />
        Your browser does not support the audio element.
      </audio>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import TrInput from '../TrInput/TrInput';
import TrBox from '../../GameBaseElements/TrBox/TrBox';
import TrStaticText from '../TrInput/TrStaticText';
import * as utils from '../../Utils/inputUtils';
import defaultMixin from '../../mixins/defaultMixin';
import inputMixin from '../../mixins/inputMixin';
import { log } from 'util';
/**
 *
 * Pozycja elementu
 * @typedef {Object}  Pos
 *
 * @property {Number}  x  - x
 * @property {Number}  y  - y
 */

/**
 * Konfiguracja ramki z wyrazami
 * @typedef BoxConfig
 *
 * @property {Number} fontSize - wielkośc czcionki
 * @property {Number} height - wysokość ramki
 * @property {Object} cssStyle - indywidualny styl CSS, którym można zmienić wygląd elementu
 *
 * @example
 *    boxConfig: {
 *     fontSize: 25,
 *     height: 10,
 *     cssStyle: {
 *       top: '29%',
 *       left: '37%',
 *       width: '20%',
 *       height: '10%',
 *       'flex-wrap': 'wrap',
 *       padding: '0',
 *       background: '#e7e7e7',
 *     },
 *   },
 *
 */

/**
 * Obiekt konfiguracji obrazka/grafiki
 * @typedef ImageData
 *
 * @property {String} imagePath - ścieżka do pliku obrazka
 * @property {Number} width - szerokośc
 * @property {Number} height - wysokość
 * @property {Object} cssStyle - indywidualny styl CSS, którym można zmienić wygląd elementu
 *
 * @example
 * (...)
 *   shuffle: true,
 *   pick: 4,
 *   items: [
 *     {
 *       items: [
 *         {
 *           imageData: {
 *             imagePath: 'games/page024_game0/img/00.jpg',
 *             width: 1500 / 3,
 *             height: 1000 / 3,
 *           },
 *           id: 'input_00',
 *           correct: ['soda żrąca soda kaustyczna', 'soda kaustyczna soda żrąca', 'soda żrąca', 'soda kaustyczna'],
 *         },
 *       ],
 *     },
 * (...)
 */

/**
 * Konfigurowanie tła
 * @typedef {Object} MainImage
 *
 * @property {String} imagePath  - ściezka do pliku tła (od **game** )
 * @property {String} position - typ pozycji w formacie CSS,
 * @property {Pos} pos  - pozycja
 * @property {Number} width  - szerokość tła
 * @property {Number} height  - wysokość tła
 * @property {Object} cssStyle - indywidualny styl CSS, którym można zmienić wygląd elementu
 *
 * @example
 *   mainImage: {
 *   imagePath: 'games/page076_game0/img/bcg.png',
 *   pos: {
 *     x: 0,
 *     y: 0,
 *   },
 *   width: 1920,
 *   height: 1080,
 * },
 */

/**
 * @namespace InputOnBackgroud
 */

/**
 * Pojedyńczy konfigurowalny input z InputOnBackgroundConfig. W stosunku do zwykłego zawiera dodatkowo mozliwośc zagnieżdzania tablicy **Items**
 * @typedef {Object}  InputConfig
 * @memberof InputOnBackgroud
 *
 * @property {String}  id - ID inputa
 * @property {BoxConfig}  boxConfig - konfiguracja ramki z wyrazami
 * @property {ImageData}  imageData - konfiguracja obrazka
 * @property {Array.<String>|String}  correct - tablica poprawnych odpowiedzi
 * @property {Boolean}  exclude - jeśli true dany input zostanie wyłączony z procesu sprawdzania (nie będzie brany pod uwagę)
 * @property {Number}  inputWidth  - szerokość inputów
 * @property {Number}  inputHeight - wysokość inputów
 * @property {Number}  inputFontSize - wielkość czcionki inputów
 * @property {Number}  inputMaxLength - maksymalna ilość znaków możliwyh do wprowadzenia
 * @property {Array}  items - tablica konfigurowanych inputów jeśli wewnątrz jest kolejna tablica typu {@link InputOnBackgroud.InputConfig}
 * @property {Object} cssStyle - indywidualny styl CSS, którym można zmienić wygląd elementu
 *
 * @example
 *
 * (...)
 *
 *  inputWidth: 220,
 *  inputHeight: 78,
 *  inputFontSize: '2.3rem',
 *  inputMaxLength: 34,
 *  shuffle: false,
 *  pick: 2,
 *  items: [
 *    {
 *      id: 'input_1',
 *      pos: {
 *        y: 469,
 *        x: 306,
 *      },
 *      inputWidth: 1301,
 *      inputHeight: 118,
 *      correct: ['test1','test2'],
 *      exclude: true,
 *    },
 *    {
 *      id: 'input_2',
 *      pos: {
 *        y: 869,
 *        x: 306,
 *      },
 *      inputWidth: 1301,
 *      inputHeight: 118,
 *      correct: ['test','Test'],
 *    },
 *  ]
 *
 * (...)
 */

/**
 * Konfiguracja gier opartych na silniku inputOnBackground
 * @typedef InputOnBackgroundConfig
 *
 * @property {MainImage}  mainImage - konfogurowalne tło
 * @property {Array<Pos>} layoutItems - tablica położeń dla inputów
 * @property {Array.<Pos>} layoutImages- tablica położeń dla obrazków
 * @property {Boolean} caseSensitive - czy inputy mają być wrażliwe na wielkośc znaków
 * @property {Number}  inputWidth  - defaultowa szerokość inputów
 * @property {Number}  inputHeight - defaultowa wysokość inputów
 * @property {Number}  inputFontSize - defaultowa wielkość czcionki inputów
 * @property {Number}  inputMaxLength - defaultowa maksymalna ilość znaków możliwyh do wprowadzenia
 * @property {Boolean}  shuffle - czy tablica **items** ma się losować
 * @property {Number}  pick  - ilość elementów do wybrania
 * @property {Array.<InputOnBackgroud.InputConfig>} items -  tablica elementów
 */

/**
 * Konfiguracja gier opartych na silniku inputOnBackground
 * @type {InputOnBackgroundConfig}
 * @memberof InputOnBackgroud
 * @name InputOnBackgroundConfig
 */
var config;

export default {
  name: 'tr-input-on-background',
  components: {
    TrInput,
    TrBox,
    TrStaticText,
  },

  mixins: [inputMixin, defaultMixin],
  props: {
    startGameConfig: {
      type: Object,
      default: function () {
        return {};
      },
    },
  },
  data() {
    return {
      words: [],
      boxConfig: {},
      inputData: [],
      staticText: [],
      imageData: [],

      showPlayer: false,
      whiteColor: false,
      player: {},
      destroyAudio: false,
      componentKey: true,
      audioFile: '',
    };
  },
  beforeMount() {
    this.init();
  },
  mounted() {},
  methods: {
    cancelCheck() {
      this.$store.commit('CHANGE_CHECK_SWITCH', false);
    },
    init() {
      this.items = this.prepareData(this.startGameConfig.items);
      this.inputData = _.map(this.items, (item) => {
        return {
          correct: item.correct,
          val: item.value || '',
          block: item.block || false,
          inputStatus: item.inputStatus || null,
          caseSensitive: item.caseSensitive || this.caseSensitive,
          coupled: item.coupled,
          mathematica: item.mathematica,
          replace: item.replace,
          exclude: item.exclude,
        };
      });

      this.longestWordLength = _.maxBy(this.inputData, (item) => {
        if (item.correct && _.isArray(item.correct)) {
          return item.correct.length;
        }
        return false;
      });
      if (this.startGameConfig.showPlayer) this.showPlayer = true;
      if (this.startGameConfig.audioSrc) this.audioFile = this.startGameConfig.audioSrc;
      if (this.startGameConfig.whiteColor) this.whiteColor = this.startGameConfig.whiteColor;
    },
    getMainImage(baseConfig) {
      return baseConfig.mainImage;
    },
    getAllImage(items) {
      let mainImage = this.getMainImage(this.startGameConfig);
      let filterItems = _.map(_.filter(items, 'imageData'), (item) => {
        return item.imageData;
      });
      return _.isObject(mainImage) ? _.compact([mainImage, ...filterItems]) : _.compact(filterItems);
    },
    processData(node, staticText, imageArray) {
      if (node.items) {
        // if(status.lengt>0){}
        if (node.pick && node.shuffle) {
          node.items = _.sampleSize(_.cloneDeep(node.items), node.pick);
        } else if (node.pick) {
          node.items = _.take(_.cloneDeep(node.items), node.pick);
        }
        if (node.shuffle) {
          node.items = _.shuffle(node.items);
        }
        if (node.staticText) {
          staticText.push(node.staticText);
        }
        if (node.imageData) {
          imageArray.push(node.imageData);
        }
        return _.flattenDeep(
          _.map(node.items, (item) => {
            return this.processData(item, staticText, imageArray, node);
          })
        );
      }
      // let status = _.filter(node.items, 'staticIndex');
      if (node.staticText) {
        staticText.push(node.staticText);
      }
      if (node.imageData) {
        imageArray.push(node.imageData);
      }
      return node;
    },
    prepareData() {
      let staticText = [];
      let imageArray = [];
      let pick = this.startGameConfig.pick;
      let data = _.cloneDeep(this.startGameConfig.items);
      let staticIndexArray = _.filter(data, (item) => {
        return !_.isNil(item['staticIndex']);
      });
      if (staticIndexArray.length > 0) {
        pick = this.startGameConfig.pick - staticIndexArray.length;
        data = _.difference(data, staticIndexArray);
      }
      if (
        this.startGameConfig.items &&
        this.startGameConfig.pick &&
        _.isNumber(this.startGameConfig.pick) &&
        this.startGameConfig.pick > 0 &&
        this.startGameConfig.shuffle
      ) {
        data = _.sampleSize(_.cloneDeep(data), pick);
      } else if (
        this.startGameConfig.items &&
        this.startGameConfig.pick &&
        !_.isNumber(this.startGameConfig.pick) &&
        this.startGameConfig.shuffle
      ) {
        data = _.sampleSize(_.cloneDeep(data));
      } else if (this.startGameConfig.items && data.picks && _.isArray(data.picks)) {
        let counter = 0;
        data = _.flattenDeep(
          _.map(_.omit(data, ['picks']), (element) => {
            if (_.isArray(element) && data.picks && data.picks[counter]) {
              return _.flattenDeep(
                _.map(_.sampleSize(_.cloneDeep(element), data.picks[counter++]), (elem) => {
                  return [...elem.items];
                })
              );
            } else if (_.isArray(element)) {
              return [...element.items];
            }
          })
        );
      } else if (this.startGameConfig.items && pick) {
        data = _.take(_.cloneDeep(data), pick);
      } else if (this.startGameConfig.items && this.startGameConfig.shuffle) {
        data = _.shuffle(data);
      }
      _.each(staticIndexArray, (item) => {
        data.push(item);
      });
      data = _.flattenDeep(
        _.map(data, (item) => {
          return this.processData(item, staticText, imageArray, this.startGameConfig);
        })
      );
      /**
       * get image from first layer processed items
       */
      this.imageData = imageArray;
      if (this.startGameConfig.mainImage) {
        this.imageData.unshift(this.startGameConfig.mainImage);
      } else if (this.startGameConfig.background) {
        this.imageData = Object.assign({}, { imagePath: this.startGameConfig.background });
      }
      /** add static text */
      if (this.startGameConfig.staticText) {
        if (
          this.startGameConfig.staticText[0] &&
          this.startGameConfig.staticText[0].items &&
          this.startGameConfig.staticText[0].items.length > 0
        ) {
          if (this.startGameConfig.staticText[0].pick) {
            staticText = _.map(this.startGameConfig.staticText, (elem) => {
              return _.take(_.cloneDeep(elem.items), elem.pick);
            });
            staticText = _.flatten(staticText);
          }
        }
        staticText = this.startGameConfig.staticText;
      }

      this.staticText = _.map(staticText, (item, index) => {
        if (this.startGameConfig.layoutStaticText) {
          return Object.assign(item, this.startGameConfig.layoutStaticText[index]);
        } else {
          return item;
        }
      });

      /**
       * add layout for items if exist
       * @namespace InputOnBackgroud
       * @method if
       * @param  {array} this.startGameConfig.layoutItems layoutItems array
       * @return {Boolean}
       */
      if (this.startGameConfig.layoutItems) {
        this.items = _.map(_.cloneDeep(this.items), (item, index) => {
          return Object.assign(item, this.startGameConfig.layoutItems[index]);
        });
      }
      let tail = _.cloneDeep(this.imageData);
      let head;
      /**
       * add layout for images if exist
       * @namespace InputOnBackgroud
       * @method if
       * @param  {array} this.startGameConfig.layoutImages layoutImages array
       * @return {Boolean}
       */
      if (this.startGameConfig.layoutImages) {
        /** if first elementr is mainImage don't applay layout to him */
        if (this.startGameConfig.mainImage) {
          head = this.imageData[0];
          tail = _.tail(tail);
        }
        /** rest of array */
        tail = _.map(_.cloneDeep(tail), (item, index) => {
          return Object.assign(item, this.startGameConfig.layoutImages[index]);
        });
      }
      if (this.startGameConfig.mainImage) {
        this.imageData = [head, ...tail];
      } else {
        this.imageData = tail;
      }
      this.imageData = _.compact(this.imageData);

      this.textItem = _.map(_.cloneDeep(data), (item, index) => {
        if (this.startGameConfig.layoutItems && this.startGameConfig.layoutItems[index]) {
          return _.defaultsDeep(item, _.cloneDeep(this.startGameConfig.layoutItems[index]));
        }
        return item;
      });
      /**
       * add words box to game
       */
      if (this.startGameConfig.words) {
        let syncData = this.startGameConfig.boxConfig.syncData;
        this.words = this.startGameConfig.words;
        if (this.startGameConfig.boxConfig && syncData) {
          this.words = _.intersection(
            this.startGameConfig.words,
            _.flattenDeep(
              _.map(this.textItem, (item) => {
                if (item[syncData]) {
                  return [...item[syncData]];
                } else if (item.markers) {
                  return _.flattenDeep(
                    _.map(item.markers, (item) => {
                      return [...item[syncData]];
                    })
                  );
                }
                return [...item[syncData]];
              })
            )
          );
        }
        this.words = _.shuffle(this.words);
      }
      return _.cloneDeep(this.textItem);
    },
    /**
     * reset all containerEnd without reset reactivity
     * @namespace InputOnBackgroud
     * @method resetGameAction
     */
    resetGameAction() {
      this.init();
    },
    checkGameAction() {
      return this.inputOnBackgroundCheckGameAction();
    },
    /**
     * test if object objectExist
     * @method objectExist
     * @namespace InputOnBackgroud
     * @param  {Object}    item testiing element
     * @return {Boolean}         true if is object
     */
    objectImageExist(item) {
      return _.isObject(item) && !_.isNil(item.imagePath);
    },
    getImage(item) {
      return 'static/' + item.imagePath;
    },
    getImageStyle(imageData) {
      if (_.isObject(imageData) && imageData.imagePath) {
        if (!imageData.pos) {
          imageData = Object.assign({}, imageData, {
            pos: {
              x: 0,
              y: 0,
            },
          });
        }
        if (!imageData.height) {
          imageData = Object.assign({}, imageData, {
            height: 358,
          });
        }
        if (!imageData.width) {
          imageData = Object.assign({}, imageData, {
            width: 1850,
          });
        }
      } else {
        return '';
      }
      return _.merge({ position: 'absolute' }, this.getCustomStyle(imageData), {
        'background-image': `url(${require(`__staticDir/${this.$store.getters.multibookType}/${imageData.imagePath}`)})`,
        top: utils.elementTopPosition(imageData.pos.y, imageData.offsetVertical),
        left: utils.elementLeftPosition(imageData.pos.x, imageData.offsetHorizontal),
        width: utils.elementWidth(imageData.width),
        height: utils.elementHeight(imageData.height),
      });
    },
    getStaticTextStyle(staticTextElement) {
      staticTextElement.offsetVertical = staticTextElement.offsetVertical
        ? staticTextElement.offsetVertical
        : this.startGameConfig.staticTextElementOffsetVertical;
      staticTextElement.offsetHorizontal = staticTextElement.offsetHorizontal
        ? staticTextElement.offsetHorizontal
        : this.startGameConfig.staticTextElementOffsetHorizontal;
      staticTextElement.width = staticTextElement.width
        ? staticTextElement.width
        : this.startGameConfig.staticTextElementWidth;
      staticTextElement.height = staticTextElement.height
        ? staticTextElement.height
        : this.startGameConfig.staticTextElementHeight;
      return _.merge({ position: 'absolute' }, this.getCustomStyle(staticTextElement), {
        // 'position': 'absolute',
        top: utils.elementTopPosition(staticTextElement.pos.y, staticTextElement.offsetVertical),
        left: utils.elementLeftPosition(staticTextElement.pos.x, staticTextElement.offsetHorizontal),
        width: utils.elementWidth(staticTextElement.width),
        height: utils.elementHeight(staticTextElement.height),
      });
    },
    getBoxStyle(box) {
      // box.x = _box.x;
      // box.y = _box.y;
      // box.width = box.width;
      // box.height = box.height;
      return _.merge({ position: 'absolute' }, this.getCustomStyle(box), {
        // 'position': 'absolute',
        left: utils.elementLeftPosition(box.x, 0, '%'),
        top: utils.elementTopPosition(box.y, 0, '%'),
        width: utils.elementWidth(box.width, '%'),
        height: utils.elementHeight(box.height, '%'),
      });
    },
    getStyle(item) {
      let width = '1920px';
      let height = '1080px';
      if (item && item.cssStyle) {
        (width = item.cssStyle.width || width), (height = item.cssStyle.height || height);
      }
      return _.merge({}, { width: width, height: height }, this.getCustomStyle(item));
    },
    getCustomStyle(item) {
      let customStyle = {};
      if (item && item.cssStyle) {
        if (_.isObject(item.cssStyle)) {
          customStyle = _.mapKeys(item.cssStyle, function (value, key) {
            return _.toString(key);
          });
        }
      }
      return customStyle;
    },
    audioPlayerStyle(item) {
      if (item) {
        return this.getCustomStyle(item);
      }
      return {};
    },
    giveMeAudio(audioFile) {
      return require(`__staticDir/${this.$store.getters.multibookType}/${audioFile}`);
    },
  },
  computed: {
    caseSensitive() {
      return this.startGameConfig.caseSensitive;
    },
    maxCharNumber() {
      return _.round((this.longestWordLength.correct.length * 120) / 100).toString();
    },
    backgroundImageUrl() {
      return 'static/' + this.startGameConfig.background;
    },
    boxConfigFontSize() {
      let fontSize = this.startGameConfig.boxConfig && this.startGameConfig.boxConfig.fontSize;
      return fontSize + 'px';
    },
  },
  created() {
    this.boxConfig = {
      x: (this.startGameConfig.boxConfig && this.startGameConfig.boxConfig.left) || 1,
      y: (this.startGameConfig.boxConfig && this.startGameConfig.boxConfig.top) || 14,
      width: (this.startGameConfig.boxConfig && this.startGameConfig.boxConfig.width) || 98,
      height: (this.startGameConfig.boxConfig && this.startGameConfig.boxConfig.height) || 12,
      cssStyle: (this.startGameConfig.boxConfig && this.startGameConfig.boxConfig.cssStyle) || {},
      unit: '%',
    };
  },
};
</script>

<style lang="scss" scoped>
.game-input-on-background-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  position: inherit;
  // background-image: url("~@/assets/icons/book/toolbox/przybornik_kosz.png");
  .main-image-container {
    position: relative;
    display: block;
    width: 100%;
    height: 100%;

    background-size: contain;
    background-repeat: no-repeat;
  }
}
.audio {
  z-index: 1;
  display: block;
  width: 60%;
  height: auto;
  // position: relative;
}
#playerAudio {
  width: 100%;
}

.audioWhite::-webkit-media-controls-panel {
  background-color: #fff;
}

.audioWhite::-webkit-media-controls-mute-button {
  background-color: #f0f0f0;
  border-radius: 50%;
}
</style>
